import { useEffect, useState } from "react";
import {
    Dialog, DialogContent, DialogActions, Button, Box, Avatar, Typography,
    TextField, InputAdornment, Checkbox, Stack,
    CircularProgress, Alert, FormControlLabel, Divider, Pagination, Skeleton,
} from "@mui/material";
import { Groups, Search, Send } from "@mui/icons-material";
import { alpha, useTheme } from "@mui/material/styles";
import { DialogProps } from "@toolpad/core/useDialogs";
import { useApiClient } from "../../ApiClientProvider";
import { InviteService, IEventCommunityShareResult, IEventShareDestination, CommunityShareMode } from "../../services/inviteService";
import { getApiErrorMessage } from "../../utils/apiErrorUtils";

export interface IShareToCommunitiesPayload {
    eventId: string;
    eventTitle?: string;
    isInviteOnly?: boolean;
    mode?: CommunityShareMode;
}

const ShareToCommunitiesDialog: React.FC<DialogProps<IShareToCommunitiesPayload, IEventCommunityShareResult[] | null>> = ({
    payload,
    open,
    onClose,
}) => {
    const [communities, setCommunities] = useState<IEventShareDestination[]>([]);
    const [loadingCommunities, setLoadingCommunities] = useState(false);
    const [search, setSearch] = useState('');
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [message, setMessage] = useState('');
    const [notifyMembers, setNotifyMembers] = useState(true);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [listError, setListError] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [pageCount, setPageCount] = useState(0);
    const [reload, setReload] = useState(0);
    const apiClient = useApiClient('applicationClient');
    const theme = useTheme();
    const mode = payload?.mode ?? 'organiser';

    useEffect(() => {
        if (!open || !payload?.eventId) return;
        const controller = new AbortController();
        setLoadingCommunities(true);
        setListError(null);
        const service = new InviteService(apiClient);
        service.getShareDestinations(payload.eventId, mode, page, search, controller.signal)
            .then(result => {
                if (controller.signal.aborted) return;
                setCommunities(result.items);
                setPageCount(Math.ceil(result.totalCount / result.pageSize));
            })
            .catch((cause: unknown) => {
                if (!controller.signal.aborted) {
                    setCommunities([]);
                    setListError(getApiErrorMessage(cause, 'Your communities could not load. Try again.'));
                }
            })
            .finally(() => { if (!controller.signal.aborted) setLoadingCommunities(false); });
        return () => controller.abort();
    }, [open, apiClient, payload?.eventId, mode, page, search, reload]);

    const toggleCommunity = (id: string) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(communityId => communityId !== id) : prev.length < 20 ? [...prev, id] : prev);
    };

    const handleShare = async () => {
        if (!payload?.eventId || selectedIds.length === 0 || sending || loadingCommunities || listError) return;
        setSending(true);
        setError(null);
        try {
            const inviteService = new InviteService(apiClient);
            const results = await inviteService.shareToCommunities(
                payload.eventId,
                selectedIds,
                message.trim() || undefined,
                notifyMembers,
                mode
            );
            onClose(results);
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Your event could not be shared. Try again.');
            setSelectedIds([]);
            setReload(previous => previous + 1);
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={() => { if (!sending) onClose(null); }}
            maxWidth="sm"
            fullWidth
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: '16px',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.16)',
                        overflow: 'hidden',
                    },
                },
            }}
        >
            <Box
                sx={{
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.10)} 0%, ${alpha(theme.palette.primary.main, 0.03)} 100%)`,
                    px: 3,
                    pt: 3,
                    pb: 2.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                }}
            >
                <Avatar
                    sx={{
                        width: 48,
                        height: 48,
                        bgcolor: theme.palette.primary.main,
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.35)}`,
                    }}
                >
                    <Groups />
                </Avatar>
                <Box>
                    <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                        Share to communities
                    </Typography>
                    {payload?.eventTitle && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                            {payload.eventTitle}
                        </Typography>
                    )}
                </Box>
            </Box>

            <DialogContent sx={{ px: 3, pt: 3, pb: 1 }}>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {payload?.isInviteOnly && mode === 'organiser' && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        This event is invite-only. Members of the selected communities will automatically get access,
                        whether or not you notify them below.
                    </Alert>
                )}
                {payload?.isInviteOnly && mode === 'participant' && (
                    <Alert severity="info" sx={{ mb: 2 }}>This event is invite-only. Sharing it does not give other members access.</Alert>
                )}

                <TextField
                    fullWidth
                    label="Search communities"
                    value={search}
                    onChange={event => { setSearch(event.target.value); setPage(1); }}
                    disabled={sending}
                    size="small"
                    sx={{ mb: 1.5 }}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" sx={{ color: 'text.disabled' }} />
                                </InputAdornment>
                            ),
                        },
                    }}
                />

                <Box sx={{ maxHeight: theme.spacing(50), overflowY: 'auto', py: 1 }} aria-busy={loadingCommunities}>
                    {loadingCommunities ? (
                        <Stack spacing={1} aria-label="Loading communities"><Skeleton /><Skeleton /><Skeleton /></Stack>
                    ) : listError ? (
                        <Alert severity="error" action={<Button color="inherit" onClick={() => setReload(previous => previous + 1)}>Retry</Button>}>{listError}</Alert>
                    ) : communities.length === 0 ? (
                        <Box sx={{ py: 4, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">{search.trim()
                                ? 'No communities match your search. Try another name.'
                                : 'You don\'t have any communities you can share to. Contact your organiser if you need posting access.'}</Typography>
                        </Box>
                    ) : (
                        <Stack spacing={1.25}>
                            {communities.map(community => {
                                const checked = selectedIds.includes(community.id);
                                return (
                                    <FormControlLabel key={community.id}
                                        label={community.name}
                                        sx={{ mx: 0, minHeight: theme.spacing(5.5), overflowWrap: 'anywhere' }}
                                        control={<Checkbox
                                            size="small"
                                            checked={checked}
                                            onChange={() => toggleCommunity(community.id)}
                                            disabled={sending || (!checked && selectedIds.length >= 20)}
                                        />}
                                    />
                                );
                            })}
                        </Stack>
                    )}
                </Box>
                {!loadingCommunities && !listError && pageCount > 1 && (
                    <Pagination count={pageCount} page={page} disabled={sending} onChange={(_, nextPage) => setPage(nextPage)} aria-label="Community pages" />
                )}

                <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={4}
                    label="Message (optional)"
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    disabled={sending}
                    slotProps={{ htmlInput: { maxLength: 2000 } }}
                    sx={{ mt: 2 }}
                />

                {mode === 'organiser' && <Divider sx={{ my: 2 }} />}

                {mode === 'organiser' && <FormControlLabel
                    control={<Checkbox checked={notifyMembers} disabled={sending} onChange={e => setNotifyMembers(e.target.checked)} />}
                    label={
                        <Box>
                            <Typography variant="body2" fontWeight={500}>Notify members</Typography>
                            <Typography variant="caption" color="text.secondary">
                                Send an invitation email and in-app notification to each community member.
                            </Typography>
                        </Box>
                    }
                />}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2.5 }}>
                <Button onClick={() => onClose(null)} disabled={sending}>Cancel</Button>
                <Button
                    variant="contained"
                    startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <Send />}
                    onClick={handleShare}
                    disabled={sending || loadingCommunities || Boolean(listError) || selectedIds.length === 0}
                >
                    Share to {selectedIds.length || ''} {selectedIds.length === 1 ? 'community' : 'communities'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ShareToCommunitiesDialog;
